import { gql, useQuery } from '@apollo/client'

import {
  PaymentDetailFragmentDoc,
  StorePurchaseHistoryFragmentDoc,
  StorePurchaseOrderDetailQuery,
  StorePurchaseOrderDetailQueryVariables,
  UserAddressFragmentDoc,
} from '@/clients/utoniq-core/schema'
import { useRouter } from '@/core'
import { makeViewModel } from '@/core/architecture'
import { addCollectionToCollectionFee } from '@/core/cart'
import { groupOrderItemsByCollection } from '@/core/order'

import { Action, State } from './template'

export const useViewModel = makeViewModel<State, Action>(() => {
  const { router } = useRouter<{
    purchaseOrderId: string
  }>()
  const { data, loading } = useQuery<
    StorePurchaseOrderDetailQuery,
    StorePurchaseOrderDetailQueryVariables
  >(QUERY, { variables: { id: router.query.purchaseOrderId } })

  return {
    state: {
      loading,
      userCartTransactionOrder: data?.userCartTransactionOrder ?? null,
      cartItemsByCollection: groupOrderItemsByCollection(
        data?.userCartTransactionOrder?.items
      ),
      events:
        data?.userCartTransactionOrder.items
          .map(({ item }) => item?.collection?.event)
          .filter(
            (_event, i, self) => self.indexOf(_event) === i && !!_event
          ) ?? [],
      prepaidPointBalance: data?.prepaidPointBalance ?? null,
      paymentSource: data?.paymentSource ?? null,
      fullName: data?.user?.fullName || '',
      address: data?.user?.address ?? null,
      phoneNumber: data?.user?.phoneNumber ?? '',
      isPhoneNumberVerified: !!data?.user?.isPhoneNumberVerified,
      systemFee: addCollectionToCollectionFee(
        data?.userCartTransactionOrder.checkoutFees?.collections,
        data?.userCartTransactionOrder?.items.map(({ item }) => item.collection)
      ),
      shippingFee:
        data?.userCartTransactionOrder.checkoutFees?.shipping ?? null,
      konbiniFee: data?.userCartTransactionOrder.checkoutFees?.konbini ?? null,
      refundedAmount: data?.userCartTransactionOrder.cancels?.reduce(
        (acc, { refundedAmount }) => acc + refundedAmount,
        0
      ),
      canceledAt: data?.userCartTransactionOrder.cancels
        ?.map(({ canceledAt }) => canceledAt)
        .sort((a, b) => b - a)[0],
    },
    actions: {},
  }
})

const QUERY = gql`
  query StorePurchaseOrderDetail($id: ID!, $skuId: String) {
    userCartTransactionOrder(id: $id) {
      ...StorePurchaseHistory
    }
    user {
      id
      fullName
      phoneNumber
      formattedPhoneNumber @client
      isPhoneNumberVerified
      ...UserAddress
    }
    paymentSource {
      ...PaymentDetail
    }
    prepaidPointBalance {
      lastUpdatedAt
      prepaidPointId
      balance
      formattedPrepaidPointBarance @client
    }
  }
  ${StorePurchaseHistoryFragmentDoc}
  ${PaymentDetailFragmentDoc}
  ${UserAddressFragmentDoc}
`
